import { faArrowRight, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useCallback, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"

import classNames from "classnames"
import api, { ApiFieldError } from "../../../services/api"
import Input from "../../components/input"
import useFlash from "../../hooks/use_flash"

type FormData = {
  password: string
  passwordError: string
  passwordBlurred: boolean
  passwordConfirmation: string
  passwordConfirmationError: string
  passwordConfirmationBlurred: boolean
  valid: boolean
}

export default function ResetPasswordPage() {
  const { token } = useParams()
  const { setFlash } = useFlash()
  const navigate = useNavigate()

  const [formData, setFormData] = useState<FormData>({
    password: "",
    passwordError: "",
    passwordBlurred: false,
    passwordConfirmation: "",
    passwordConfirmationError: "",
    passwordConfirmationBlurred: false,
    valid: false,
  })
  const [inProgress, setInProgress] = useState(false)

  const update = useCallback((formData: FormData): FormData => {
    const passwordInvalid = formData.password.length < 5
    const passwordsDontMatch =
      formData.password !== formData.passwordConfirmation ||
      formData.passwordConfirmation.length == 0

    const newFormData = {
      ...formData,
      passwordError: passwordInvalid ? "should be at least 12 character(s)" : "",
      passwordConfirmationError: passwordsDontMatch ? "does not match password" : "",
      valid: !passwordInvalid && !passwordsDontMatch,
    }
    setFormData(newFormData)
    return newFormData
  }, [])

  const formSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const newFormData = update({
        ...formData,
        passwordBlurred: true,
        passwordConfirmationBlurred: true,
      })

      if (newFormData.valid) {
        setInProgress(true)
        api()
          .post(`/api/accounts/reset-password/${token}`, {
            password: newFormData.password,
            password_confirmation: newFormData.passwordConfirmation,
          })
          .then(({ data }) => {
            const newFormData = { ...formData }
            newFormData.passwordError = ""
            newFormData.passwordConfirmationError = ""
            if (data.status === "error") {
              data.errors.forEach((error: ApiFieldError) => {
                if (error.field == "password") newFormData.passwordError = error.message
                else if (error.field == "password_confirmation")
                  newFormData.passwordConfirmationError = error.message
              })
              setInProgress(false)
              setFormData(newFormData)
            } else {
              setFlash({
                type: "success",
                text: "Password reset successfully. Please log in.",
              })
              navigate("/app/login")
            }
          })
          .catch((error) => {
            console.error(error)
            setFlash({
              type: "error",
              text: "Sorry, something went wrong. Please try again later, or contact support.",
            })
            setInProgress(false)
          })
      }
    },
    [update, setFlash, navigate, formData, token]
  )

  return (
    <>
      <div className="flex min-h-full flex-col justify-start py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-200">
            Reset password
          </h2>
          <div className="mt-2 text-sm text-center">
            <Link to="/app/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Log in instead
              <FontAwesomeIcon icon={faArrowRight} className="ml-1 text-xs" />
            </Link>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-900 px-4 py-8 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={formSubmit} method="POST">
              <Input
                label="New password (at least 12 characters)"
                name="password"
                type="password"
                error={formData.passwordBlurred ? formData.passwordError : null}
                onChange={(v) => {
                  update({ ...formData, password: v })
                }}
                onBlur={() => {
                  update({ ...formData, passwordBlurred: true })
                }}
                disabled={inProgress}
                value={formData.password}
              />
              <Input
                label="Confirm new password"
                name="password_confirmation"
                type="password"
                error={
                  formData.passwordConfirmationBlurred ? formData.passwordConfirmationError : null
                }
                onChange={(v) => {
                  update({ ...formData, passwordConfirmation: v })
                }}
                onBlur={() => {
                  update({ ...formData, passwordConfirmationBlurred: true })
                }}
                disabled={inProgress}
                value={formData.passwordConfirmation}
              />
              <div>
                <button
                  type="submit"
                  className={classNames(
                    "flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-500 disabled:ring-gray-500"
                  )}
                  disabled={inProgress}
                >
                  {inProgress && (
                    <FontAwesomeIcon icon={faSpinner} className="text-white mt-1 mr-2" spin />
                  )}
                  Change password
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
